import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import hrApi from "./hrApi";
import SignaturePad from "./SignaturePad";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NameInputs } from "./NameInputs";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, XCircle, Download, BookOpen, GraduationCap, ChevronLeft, ChevronRight, FileText, PartyPopper } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const img = (n) => `${BASE}/api/hr/public/swi-img/${n}`;
const LET = "ABCDEFGH";
function Shell({ children }) { return <div className="min-h-screen bg-slate-100 py-6 px-2 sm:px-4"><div className="max-w-3xl mx-auto bg-white rounded-3xl shadow-sm p-4 sm:p-6">{children}</div></div>; }

function ReadStage({ token, d, onDone }) {
  const [shown, setShown] = useState(1);
  const last = shown >= d.pages;
  return (
    <div className="space-y-3" data-testid="swi-read">
      <p className="text-sm text-slate-600 inline-flex items-center gap-1.5"><BookOpen size={15} /> Read the work instruction carefully. The quiz is next.</p>
      {Array.from({ length: shown }).map((_, i) => (
        <img key={i} src={`${BASE}/api/hr/public/swi/${token}/page/${i}`} alt={`Page ${i + 1}`} className="w-full rounded-lg border border-slate-200" data-testid={`swi-page-${i}`} />
      ))}
      {!last ? <Button data-testid="swi-next-page" onClick={() => setShown(shown + 1)} className="w-full h-12 rounded-xl">Next page ({shown + 1} of {d.pages})</Button>
        : <Button data-testid="swi-start-quiz" onClick={onDone} className="w-full h-12 rounded-xl bg-indigo-600">I have read it — start the quiz</Button>}
    </div>
  );
}

function Original({ token, n }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="text-center">
      <button data-testid="swi-original-toggle" onClick={() => setOpen(!open)} className="text-xs text-slate-500 underline inline-flex items-center gap-1"><FileText size={12} /> {open ? "Hide" : "View"} original document</button>
      {open && <div className="mt-3 space-y-2" data-testid="swi-original">{Array.from({ length: n }).map((_, i) => <img key={i} src={`${BASE}/api/hr/public/swi/${token}/page/${i}`} alt={`Page ${i + 1}`} className="w-full rounded-lg border border-slate-200" />)}</div>}
    </div>
  );
}

function LessonStage({ token, d, onDone }) {
  const [i, setI] = useState(0);
  const L = d.lessons[i];
  const last = i === d.lessons.length - 1;
  return (
    <div className="space-y-4" data-testid="swi-lesson">
      <div className="flex gap-1.5 justify-center">{d.lessons.map((_, k) => <span key={k} className={`h-2.5 rounded-full transition-[width,background-color] duration-300 ${k === i ? "w-8 bg-indigo-500" : k < i ? "w-2.5 bg-indigo-300" : "w-2.5 bg-slate-200"}`} />)}</div>
      <div key={L.id} className="swi-in rounded-3xl border border-slate-100 overflow-hidden bg-white">
        {L.image && <img src={img(L.image)} alt="" className="w-full aspect-[3/2] object-cover bg-slate-50" data-testid={`swi-lesson-img-${i}`} />}
        <div className="p-5 space-y-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-indigo-500">Lesson {i + 1} of {d.lessons.length}</div>
          <h2 className="font-heading font-extrabold text-xl text-slate-800" data-testid="swi-lesson-title">{L.title}</h2>
          <ul className="space-y-2">{L.body.split("\n").filter(Boolean).map((t, k) => <li key={k} className="flex gap-2 text-[15px] text-slate-700 leading-relaxed"><CheckCircle2 size={18} className="text-teal-500 shrink-0 mt-0.5" />{t}</li>)}</ul>
        </div>
      </div>
      <div className="flex gap-2">
        {i > 0 && <Button data-testid="swi-lesson-back" variant="outline" onClick={() => setI(i - 1)} className="h-12 rounded-xl px-4"><ChevronLeft size={18} /></Button>}
        {!last ? <Button data-testid="swi-lesson-next" onClick={() => setI(i + 1)} className="flex-1 h-12 rounded-xl gap-1 bg-indigo-600 hover:bg-indigo-700">Next <ChevronRight size={18} /></Button>
          : <Button data-testid="swi-start-quiz" onClick={onDone} className="flex-1 h-12 rounded-xl bg-indigo-600">I've got it. Start the quiz</Button>}
      </div>
      <Original token={token} n={d.pages} />
    </div>
  );
}

function QuizStage({ token, d, onDone }) {
  const [idx, setIdx] = useState(d.progress);
  const [wrong, setWrong] = useState(d.questions[d.progress]?.wrong || []);
  const [fb, setFb] = useState(null);
  const [busy, setBusy] = useState(false);
  const q = d.questions[idx];
  const pick = async (c) => {
    if (busy || wrong.includes(c) || fb?.correct) return;
    setBusy(true);
    try {
      const r = await hrApi.post(`/public/swi/${token}/answer`, { question_id: q.id, choice: c });
      if (r.data.correct) setFb({ correct: true, choice: c, explain: r.data.explain });
      else { setWrong((w) => [...w, c]); setFb({ correct: false, choice: c, explain: r.data.explain, title: r.data.lesson_title, image: r.data.lesson_image, n: Date.now() }); }
    } catch (e) { toast.error(e.response?.data?.detail || "Please try again"); }
    setBusy(false);
  };
  const next = () => {
    if (idx + 1 >= d.questions.length) return onDone();
    setIdx(idx + 1); setWrong(d.questions[idx + 1]?.wrong || []); setFb(null);
  };
  return (
    <div className="space-y-4" data-testid="swi-quiz">
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-indigo-500 transition-[width] duration-300" style={{ width: `${(idx / d.questions.length) * 100}%` }} /></div>
      <div className="text-xs font-semibold text-slate-500" data-testid="swi-q-count">Question {idx + 1} of {d.questions.length}</div>
      {q.image && <img key={q.id} src={img(q.image)} alt="" className="swi-in w-full aspect-[3/2] object-cover rounded-2xl bg-slate-50" data-testid="swi-q-img" />}
      <h2 className="font-heading font-bold text-lg text-slate-800" data-testid="swi-q-text">{q.q}</h2>
      <div className="space-y-2">
        {q.options.map((o, c) => {
          const isWrong = wrong.includes(c), isRight = fb?.correct && fb.choice === c;
          return (
            <button key={`${c}-${isWrong && fb?.choice === c ? fb.n : ""}`} data-testid={`swi-opt-${c}`} disabled={isWrong || fb?.correct} onClick={() => pick(c)}
              className={`w-full text-left rounded-xl border-2 px-4 py-3.5 text-[15px] flex gap-3 items-start transition-colors ${isRight ? "border-teal-500 bg-teal-50" : isWrong ? "border-rose-200 bg-rose-50 text-slate-400 line-through" : "border-slate-200 hover:border-indigo-400 active:bg-indigo-50"} ${isWrong && fb?.choice === c ? "swi-shake" : ""}`}>
              <span className="font-bold">{LET[c]})</span><span className="flex-1">{o}</span>
              {isRight && <CheckCircle2 size={18} className="text-teal-600" />}{isWrong && <XCircle size={18} className="text-rose-500" />}
            </button>
          );
        })}
      </div>
      {fb && <div data-testid="swi-feedback" className={`swi-in rounded-xl p-3 text-sm ${fb.correct ? "bg-teal-50 text-teal-800" : "bg-orange-50 text-orange-900"}`}>
        {!fb.correct && fb.image && <div className="flex gap-3 items-center mb-2"><img src={img(fb.image)} alt="" className="w-20 h-14 rounded-lg object-cover" data-testid="swi-feedback-img" /><span className="text-xs font-semibold">Remember: {fb.title}</span></div>}
        <b>{fb.correct ? "Correct. " : "Not quite. "}</b>{fb.explain}{!fb.correct && <div className="mt-1 font-semibold">Choose the correct answer to continue.</div>}
      </div>}
      {fb?.correct && <Button data-testid="swi-next" onClick={next} className="w-full h-12 rounded-xl">{idx + 1 >= d.questions.length ? "Continue to sign" : "Next question"}</Button>}
    </div>
  );
}

function SignStage({ token, d, onDone }) {
  const [name, setName] = useState(d.employee_name && d.employee_name !== "Preview" ? d.employee_name : "");
  const [agree, setAgree] = useState(false);
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim() || !agree) return toast.error("Type your first and last name and tick the declaration");
    if (!sig) return toast.error("Please draw your signature");
    setBusy(true);
    try { await hrApi.post(`/public/swi/${token}/submit`, { typed_name: name, agreed: agree, signature_data: sig }); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    setBusy(false);
  };
  return (
    <div className="space-y-4" data-testid="swi-sign">
      <div className="swi-in rounded-2xl bg-teal-50 text-teal-800 p-4 text-center" data-testid="swi-celebrate"><PartyPopper className="mx-auto text-teal-600 mb-1" size={30} /><div className="font-heading font-extrabold text-xl">{d.questions.length}/{d.questions.length}, well done!</div><div className="text-sm">Last step: sign below.</div></div>
      <p className="text-sm font-semibold text-slate-700 uppercase leading-relaxed">I, the undersigned, confirm that I have completed the training in the task outlined in this document and am fully aware of possible risks to my safety and to the safety of others should I fail to comply with any of these instructions.</p>
      <label className="flex items-start gap-2 text-sm"><input type="checkbox" data-testid="swi-agree" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="w-4 h-4 mt-0.5 accent-indigo-500" /> I agree to this declaration</label>
      <NameInputs value={name} onChange={setName} testid="swi-name" />
      <div><label className="text-xs font-semibold text-slate-600">Your signature *</label><SignaturePad onChange={setSig} /></div>
      <Button data-testid="swi-submit" disabled={busy} onClick={submit} className="w-full h-12 rounded-xl bg-indigo-600">{busy ? "Signing…" : "Sign & submit"}</Button>
      <p className="text-[11px] text-slate-400 text-center">Your name, time and IP are recorded for the audit trail.</p>
    </div>
  );
}

export default function SwiPage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [stage, setStage] = useState(null);
  const load = () => hrApi.get(`/public/swi/${token}`).then((r) => {
    setD(r.data);
    const x = r.data;
    setStage(x.status === "completed" ? "done" : !x.read_done ? "read" : x.progress < x.questions.length ? "quiz" : "sign");
  }).catch((e) => setErr(e.response?.data?.detail || "This link is not valid."));
  useEffect(() => { load(); }, [token]);
  if (err) return <Shell><p data-testid="swi-error" className="text-center text-slate-600">{err}</p></Shell>;
  if (!d) return <Shell><p className="text-center text-slate-400">Loading…</p></Shell>;
  const startQuiz = () => hrApi.post(`/public/swi/${token}/read`).then(() => setStage("quiz"));
  return (
    <Shell>
      <div className="space-y-4" data-testid="swi-page">
        <div className="flex items-center gap-2 text-indigo-600 text-xs font-bold uppercase tracking-wider"><GraduationCap size={16} /> Training · G &amp; L One</div>
        <h1 className="font-heading font-extrabold text-2xl text-slate-800">{d.module_name}</h1>
        {d.is_test && <div className="text-xs rounded-lg bg-orange-50 text-orange-800 px-3 py-2">Preview: nothing is filed to an employee.</div>}
        {stage === "read" && (d.layout === "lesson" ? <LessonStage token={token} d={d} onDone={startQuiz} /> : <ReadStage token={token} d={d} onDone={startQuiz} />)}
        {stage === "quiz" && <QuizStage token={token} d={d} onDone={() => setStage("sign")} />}
        {stage === "sign" && <SignStage token={token} d={d} onDone={load} />}
        {stage === "done" && <div className="text-center space-y-3" data-testid="swi-done">
          <div className="w-14 h-14 rounded-full bg-teal-100 grid place-items-center mx-auto"><ShieldCheck className="text-teal-600" size={28} /></div>
          <p className="text-slate-600">Training complete and signed. It has been filed with G &amp; L One.</p>
          <a href={`${BASE}/api/hr/public/swi/${token}/signed`} data-testid="swi-download" className="inline-flex items-center gap-2 rounded-2xl border border-teal-200 bg-teal-50 px-4 h-12 text-sm font-semibold text-teal-700"><Download size={16} /> Download your signed copy</a>
        </div>}
      </div>
    </Shell>
  );
}
